import { ENERGIES, USAGES, isValidDjclim, isValidDju, isValidRatio, setUsageRatio, usageRatio } from './domain';
import { parseClipboard, parseNum } from './sheet';
import type { DegreeDays, RatioTable } from './types';

export interface ImportResult {
  degrees: DegreeDays;
  ratios: RatioTable;
  /** Nombre de valeurs de ratios reprises. */
  applied: number;
  /** Lignes non reconnues (typologie inconnue, valeur hors plage…). */
  skipped: string[];
  djuFound: boolean;
  djclimFound: boolean;
}

const norm = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]/g, '');

const USAGE_BY_LABEL = new Map(USAGES.map((u) => [norm(u.label), u.key]));

/**
 * Lit un export tableur de ratios (CSV ou TSV).
 * Lignes reconnues : `DJU;2500`, `DJClim;110`, puis `Typologie;Chauffage;ECS;Froid`.
 */
export function parseRatioFile(text: string, base: { degrees: DegreeDays; ratios: RatioTable }): ImportResult {
  let degrees = { ...base.degrees };
  let ratios = base.ratios;
  let applied = 0;
  let djuFound = false;
  let djclimFound = false;
  const skipped: string[] = [];

  for (const cells of parseClipboard(text)) {
    const head = norm(cells[0] ?? '');
    if (head === '' && cells.length <= 1) continue;

    if (head === 'dju' || head === 'djuchauffage' || head === 'mondju') {
      const n = parseNum(cells[1] ?? '');
      if (isValidDju(n)) { degrees = { ...degrees, dju: n }; djuFound = true; } else skipped.push(cells.join(' ; '));
      continue;
    }
    if (head === 'djclim' || head === 'djclimfroid' || head === 'mondjclim') {
      const n = parseNum(cells[1] ?? '');
      if (isValidDjclim(n)) { degrees = { ...degrees, djclim: n }; djclimFound = true; } else skipped.push(cells.join(' ; '));
      continue;
    }

    const usage = USAGE_BY_LABEL.get(head);
    if (!usage) {
      if (head !== 'typologie' && head !== '') skipped.push(cells.join(' ; '));
      continue;
    }
    ENERGIES.forEach((e, i) => {
      const raw = cells[i + 1];
      if (raw === undefined || raw.trim() === '') return;
      const n = parseNum(raw);
      if (!isValidRatio(n)) { skipped.push(`${cells[0]} — ${e.label} : ${raw}`); return; }
      ratios = setUsageRatio(ratios, usage, e.key, n);
      applied += 1;
    });
  }

  return { degrees, ratios, applied, skipped, djuFound, djclimFound };
}

/** Modèle à remplir, pré-rempli avec les valeurs courantes (séparateur `;`, lisible par Excel). */
export function ratioTemplate(degrees: DegreeDays, ratios: RatioTable): string {
  const lines = [
    'DJU;' + degrees.dju,
    'DJClim;' + degrees.djclim,
    'Typologie;' + ENERGIES.map((e) => e.label).join(';'),
    ...USAGES.map((u) => {
      const r = usageRatio(ratios, u.key);
      return [u.label, ...ENERGIES.map((e) => String(r[e.key]).replace('.', ','))].join(';');
    }),
  ];
  return `\ufeff${lines.join('\r\n')}\r\n`;
}
